<template>
    <div :class="namespaceClass()">
        <mds-loader v-if="tableDataLoading"></mds-loader>
        <div :class="namespaceClass('table-container')">
            <div v-if="paginationDataLoading" :class="namespaceClass('pagination-loader')">
                <mds-loader size="medium"></mds-loader>
            </div>
            <mds-data-table
                v-if="!tableDataLoading && showRows.length > 0"
                :class="namespaceClass('data-table-container')"
                caption="List Reports"
                size="medium"
                multiselection
                row-hover
                pagination="below"
                :pagination-config="paginationOptions"
                :header-configs="this.getComponentConfig().settings.columns"
                :row-data="showRows"
                @mds-data-table-sort-change="onSort"
                @mds-data-table-page-change="onPaginateTable"
                @mds-data-table-row-selection-change="onSelectRow"
                @mds-data-table-header-select-all="onSelectAll"
            >
                <template v-slot:body-cell="cellData">
                    <template v-if="cellData.colIndex === 0">
                        <a @click="onClickRow(cellData.rowData)" :class="namespaceClass('cell-name-link')">{{
                            cellData.rowData[cellData.headerConfig.fieldName]
                        }}</a>
                    </template>
                </template>
            </mds-data-table>
            <div v-if="isEmptyDataTable" :class="namespaceClass('empty-datatable')">
                <span :class="namespaceClass('empty-state-title')">{{ getUILabel("ARCHIVE_HYPOS_EMPTY_HYPO_TITLE") }}</span>
                <span :class="namespaceClass('this-is-a-supporting')">{{ getUILabel("ARCHIVE_HYPOS_EMPTY_HYPO_DESCRIPTION") }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import HypoVueBaseMixin from "hypo-vue-base-mixin";
import { openNewTab, downloadPDF, isMSIEOrEdgeBrowser, showPDF, showPDFInIE, dateFormat, stringFormat } from "@/util";
import { MdsDataTable } from "@mds/data-table";
import MdsLoader from "@mds/loader";
import ResponsiveMixin from "@/mixins/responsive";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";

export default {
    name: "reports-dashboard",
    mixins: [HypoVueBaseMixin, MwcSharedMixin, ResponsiveMixin()],
    components: {
        MdsDataTable,
        MdsLoader
    },
    data() {
        return {
            buttonDisabled: true,
            sortBy: "modifiedTime",
            descending: true,
            keyword: "",
            pageIndex: 1,
            pageSize: 50,
            reportList: [],
            pageLoading: false,
            tableDataLoading: true,
            paginationDataLoading: false,
            firstItem: 1,
            currentPage: 1,
            currentShow: 50,
            lastItem: 0,
            paginationOptions: {
                totalItems: 100,
                showItemsInfo: true,
                showItemsSelect: false,
                pageSize: 50,
                page: 1
            },
            showRows: [],
            checkedRows: [],
            labels: this.$store.state.labels.labels,
            format: {
                date: {
                    dataType: "date",
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "numeric",
                    minute: "numeric"
                }
            },
            defaultConfig: {
                settings: {
                    columns: [
                        {
                            fieldName: "reportName",
                            align: "left",
                            width: "33%",
                            sortable: true,
                            transform: text => stringFormat(text)
                        },
                        {
                            fieldName: "hypoName",
                            align: "left",
                            width: "33%",
                            sortable: true,
                            transform: text => stringFormat(text)
                        },
                        {
                            fieldName: "modifiedTime",
                            align: "right",
                            sortable: true,
                            sorted: -1,
                            transform: dateText => dateFormat(dateText)
                        }
                    ]
                }
            }
        };
    },
    mounted() {
        this.settings.columns[0].text = this.getUILabel("archive_reports_column_report_name");
        this.settings.columns[1].text = this.getUILabel("archive_reports_column_hypo_name");
        this.settings.columns[2].text = this.getUILabel("archive_reports_column_modified_time");
        this.requestReportList().then(data => {
            this.tableDataLoading = false;
            this.showRows = data;
            this.sortData = this.showRows;
            this.lastItem = this.showRows.length;
        });
    },
    computed: {
        isEmptyDataTable() {
            return !this.paginationDataLoading && !this.tableDataLoading && this.showRows.length <= 0;
        }
    },
    watch: {
        checkedRows(newValue) {
            // Remove this constant will cause test cases failures
            this.buttonDisabled = newValue.length === 0;
            this.$emit("delete-dissabled", this.buttonDisabled);
        }
    },
    methods: {
        requestReportList() {
            const params = new URLSearchParams({
                pageIndex: this.pageIndex,
                pageSize: this.pageSize,
                sortBy: this.sortBy,
                descending: this.descending,
                keyword: this.keyword
            });
            return this.$fetch({
                url: `/api/v1/Archive?${params.toString()}`,
                method: "GET"
            }).then(respData => {
                this.paginationOptions.totalItems = respData.totalCount;
                this.paginationOptions.pageSize = this.pageSize;
                this.paginationOptions.page = this.pageIndex;
                this.reportList = respData.entities.map(item => {
                    item.id = item.reportId;
                    item.modifiedTime = this.convertUTCTimetoLocalTime(item.modifiedTime);
                    return item;
                });
                return this.reportList;
            });
        },
        convertUTCTimetoLocalTime(timeString) {
            const d = new Date(timeString);
            const utcDate = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds());
            return new Date(utcDate);
        },
        handleDownload() {
            if (this.checkedRows.length > 0) {
                this.checkedRows.map(item => {
                    this.getPDFReportData(item)
                        .then(data => {
                            downloadPDF(data);
                        })
                        .catch(err => {});
                });
            }
        },
        handleSearch(keyword) {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => {
                this.keyword = keyword;
                this.pageIndex = 1;
                this.onPaginateTable({
                    firstItem: 1,
                    lastItem: 50,
                    page: 1,
                    pageSize: 50
                });
            }, 1000);
        },
        handleDelete() {
            this.tableDataLoading = true;
            if (this.checkedRows.length <= 0) {
                return null;
            }
            return this.$fetch({
                url: `/api/v1/Archive/BatchDelete`,
                method: "POST",
                body: JSON.stringify(this.checkedRows)
            }).then(data => {
                this.tableDataLoading = false;
                this.reportList = this.reportList.filter(item => !item.selected);
                this.showRows = this.showRows.filter(item => !this.checkedRows.includes(item.reportId));
                this.checkedRows = [];
                this.onPaginateTable({
                    firstItem: 1,
                    lastItem: 50,
                    page: 1,
                    pageSize: 50
                });
            });
        },
        onSelectAll(arg) {
            this.showRows = this.showRows.map(item => {
                item.selected = arg;
                return item;
            });
        },
        onClickRow(arg) {
            this.openedHypoId = arg.id;
            const isMSIEOrEdge = isMSIEOrEdgeBrowser();
            this.paginationDataLoading = true;
            this.getPDFReportData(this.openedHypoId).then(data => {
                this.paginationDataLoading = false;
                if (isMSIEOrEdge) {
                    showPDFInIE(data, this.openedHypoId);
                } else {
                    const tab = openNewTab();
                    showPDF(tab, data);
                }
            });
        },
        onSelectRow(arg) {
            if (arg.checked) {
                this.checkedRows.push(arg.id);
            } else {
                this.checkedRows = this.checkedRows.filter(x => x !== arg.id);
            }
        },
        getPDFReportData(reportId) {
            return this.$fetch({
                url: `/api/v1/Archive/${reportId}`,
                method: "GET"
            }).then(response => {
                return response.arrayBuffer();
            });
        },
        onPaginateTable(arg) {
            this.firstItem = arg.firstItem;
            this.lastItem = arg.lastItem;
            this.currentPage = arg.page;
            this.currentShow = arg.pageSize;
            this.pageIndex = arg.page;
            this.pageSize = arg.pageSize;
            this.paginationDataLoading = true;
            this.requestReportList().then(data => {
                this.showRows = data;
                this.paginationDataLoading = false;
            });
        },
        onSort(arg) {
            const order = arg.sortOrder;
            const field = arg.sortField;
            this.sortBy = field;
            this.descending = order !== 1;
            this.pageIndex = 1;
            this.currentPage = 1;
            this.onPaginateTable({
                firstItem: 1,
                lastItem: 50,
                page: 1,
                pageSize: 50
            });
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~@mds/link";
@import "@mds/typography";
@import "~@mds/constants";
.reports-dashboard {
    &-actions {
        display: flex;
        margin-top: 27px;
        margin-bottom: 6px;
        height: 24px;
    }

    &-delete {
        margin-left: $mds-space-1-x;
    }

    &-searchBy {
        margin-bottom: 0px;
        margin-left: $mds-space-1-x;
        width: 200px;
    }

    &-report-list {
        height: calc(100vh - 226px);
    }

    &-table-container {
        position: relative;
        margin-bottom: $mds-space-6-x;
    }

    &-pagination-loader {
        position: absolute;
        top: 5%;
        left: 50%;
    }

    &-cell-name-link {
        @include mds-link($underline: false);
        cursor: pointer;
    }
    &-empty-state-title {
        @include mds-level-4-heading();
        color: $mds-background-color-dark-gray;
        margin-top: $mds-space-8-x;
    }
    &-this-is-a-supporting {
        @include mds-body-text-m();
        color: $mds-background-color-dark-gray;
        margin-top: $mds-space-1-x;
    }
    &-empty-datatable {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    ::v-deep .mds-data-table__header-cell-selection___hypo {
        display: none;
    }
}
</style>
